import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import Footer from "../../common/Footer";
import PageHeader from "../PageHeader";
import Loader from "../../common/Loader";
import DataTable from "react-data-table-component";
import { triggerAlert } from "../../utils";
import { AdminPortRequests } from "../../utils/APIClient";
import PortRequestDetailDialog from "./PortRequestDetailDialog";

/**
 * Settings > Port Requests
 *
 * Review queue for customers asking to bring an existing phone number in
 * from another carrier. Same shape as Phone Verifications, with one extra
 * transition: approving is the decision, completing records that the
 * carrier has actually finished the port. Customers chase the second one,
 * because a port takes days to weeks and nothing visible happens in
 * between.
 *
 * Approving does NOT give the customer the number. Assigning it is a
 * separate action on the customer's detail page, so it works just as well
 * for a number that never had a port request.
 */

const STATUS_TABS = [
    { key: "submitted", label: "Awaiting review" },
    { key: "approved", label: "With the carrier" },
    { key: "completed", label: "Completed" },
    { key: "rejected", label: "Rejected" },
    { key: "", label: "All" },
];

const statusBadge = (s) => {
    if (s === "completed") return <span className="badge bg-success">Completed</span>;
    if (s === "approved") return <span className="badge bg-info">With carrier</span>;
    if (s === "submitted") return <span className="badge bg-warning text-dark">Awaiting review</span>;
    if (s === "rejected") return <span className="badge bg-danger">Rejected</span>;
    return <span className="badge bg-secondary">{s || "—"}</span>;
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

/** The request stores numbers one per line; show the first plus a count. */
const summariseNumbers = (raw) => {
    const lines = String(raw || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
    if (!lines.length) return { first: "—", extra: 0 };
    return { first: lines[0], extra: lines.length - 1 };
};

export default function PortRequests() {
    document.title = "Digital Employees - Port Requests";
    const mainHeading = "Port Requests";
    const heading = "Settings / Port Requests";

    const [statusFilter, setStatusFilter] = useState("submitted");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState(null);

    const fetchRows = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await AdminPortRequests.list(
                statusFilter ? { status: statusFilter } : {},
            );
            const list = resp.data?.results || [];
            list.forEach((item, i) => (item.Num = i + 1));
            setRows(list);
        } catch (e) {
            setRows([]);
            triggerAlert("error", "Oops...", "Failed to load the port-request queue.");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => {
        fetchRows();
    }, [fetchRows]);

    const columns = useMemo(
        () => [
            { name: "#", selector: (r) => r.Num, width: "70px" },
            {
                name: "Numbers",
                grow: 2,
                selector: (r) => r.phone_numbers || "",
                cell: (r) => {
                    const { first, extra } = summariseNumbers(r.phone_numbers);
                    return (
                        <div style={{ padding: "6px 0" }}>
                            <div className="fw-semibold" style={{ fontFamily: "monospace" }}>
                                {first}
                            </div>
                            {extra > 0 && (
                                <div className="text-muted small">+{extra} more</div>
                            )}
                        </div>
                    );
                },
            },
            {
                name: "Organization",
                grow: 2,
                selector: (r) => r.org_name || "",
                sortable: true,
                cell: (r) => (
                    <div style={{ padding: "6px 0" }}>
                        <div className="fw-semibold">{r.org_name || "—"}</div>
                        <div className="text-muted small">{r.org_id}</div>
                    </div>
                ),
            },
            {
                name: "Losing carrier",
                width: "150px",
                selector: (r) => r.current_carrier || "—",
            },
            { name: "Status", width: "150px", cell: (r) => statusBadge(r.status) },
            {
                name: "Submitted",
                selector: (r) => r.submitted_at || "",
                sortable: true,
                cell: (r) => fmtDateTime(r.submitted_at),
            },
            {
                name: "Actions",
                width: "150px",
                cell: (r) => (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setDetail(r)}
                    >
                        View / Review
                    </button>
                ),
            },
        ],
        [],
    );

    const customStyles = {
        headCells: {
            style: {
                backgroundColor: "#f8f9fa",
                fontWeight: 600,
                borderTop: "1px solid #dee2e6",
                borderBottom: "1px solid #dee2e6",
            },
        },
        pagination: { style: { borderTop: "1px solid #dee2e6", padding: "8px" } },
    };

    return (
        <>
            <div id="layout-wrapper">
                <Header />
                <Sidebar />

                <div className="main-content">
                    <div className="page-content" style={{ backgroundColor: "#ededed" }}>
                        <div className="container-fluid">
                            <PageHeader mainHeading={mainHeading} heading={heading} />

                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body">
                                            <ul className="nav nav-tabs mb-3">
                                                {STATUS_TABS.map((t) => (
                                                    <li className="nav-item" key={t.key || "all"}>
                                                        <button
                                                            className={
                                                                "nav-link" +
                                                                (statusFilter === t.key ? " active" : "")
                                                            }
                                                            onClick={() => setStatusFilter(t.key)}
                                                        >
                                                            {t.label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>

                                            <div className="alert alert-info py-2 mb-3" role="alert">
                                                <strong>Reviewer checklist:</strong> the Letter of
                                                Authorization must be signed by the person named on
                                                the losing carrier's account, and the service address
                                                must match that account exactly — carriers reject
                                                ports over a mismatched suite number. Mark a request{" "}
                                                <strong>Completed</strong> only once the carrier
                                                confirms the port, then assign the number from the
                                                customer's detail page.
                                            </div>

                                            {!loading ? (
                                                <DataTable
                                                    columns={columns}
                                                    data={rows}
                                                    pagination
                                                    fixedHeader
                                                    fixedHeaderScrollHeight="600px"
                                                    persistTableHead
                                                    customStyles={customStyles}
                                                    noDataComponent={
                                                        <div className="p-4 text-muted">
                                                            No port requests match this filter.
                                                        </div>
                                                    }
                                                />
                                            ) : (
                                                <div className="loader-overlay text-white">
                                                    <Loader />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {detail && (
                <PortRequestDetailDialog
                    row={detail}
                    onClose={() => setDetail(null)}
                    onDecision={() => {
                        setDetail(null);
                        fetchRows();
                    }}
                />
            )}
        </>
    );
}
